/**
 * <s-media-gallery> — horizontal, scroll-snap product gallery.
 * Native scrolling (swipe, trackpad, shift+wheel) does the moving; this element only adds
 * previous/next buttons, thumbnail jumps, and keeps the active thumbnail and counter in sync.
 *
 *   [data-gallery-track]  scroll-snap container; each child is one slide
 *   [data-gallery-prev] / [data-gallery-next]  step buttons
 *   [data-gallery-thumb="<index>"]  thumbnail buttons
 *   [data-gallery-current]  text node for the 1-based slide number
 */
class SMediaGallery extends HTMLElement {
  #controller = null;
  #frame = 0;
  #index = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.track.addEventListener('scroll', () => this.#queueSync(), { passive: true, signal });
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.#index - 1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.#index + 1), { signal });
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => this.go(Number(thumb.dataset.galleryThumb)), { signal });
    });
    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get slides() {
    return Array.from(this.track.children);
  }

  go(index) {
    const slides = this.slides;
    if (slides.length === 0) return;
    const target = Math.max(0, Math.min(slides.length - 1, index));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Logical scroll so the step direction is correct in right-to-left languages too.
    const step = slides[target].offsetWidth + parseFloat(getComputedStyle(this.track).columnGap || '0');
    const dir = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: dir * target * step, behavior: reduce ? 'auto' : 'smooth' });
    this.#setIndex(target);
  }

  #queueSync() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  }

  #sync() {
    const first = this.slides[0];
    if (!first) return;
    const step = first.offsetWidth + parseFloat(getComputedStyle(this.track).columnGap || '0');
    this.#setIndex(Math.round(Math.abs(this.track.scrollLeft) / Math.max(step, 1)));
  }

  #setIndex(index) {
    const count = this.slides.length;
    this.#index = Math.max(0, Math.min(count - 1, index));
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = this.#index === 0;
    if (next) next.disabled = this.#index >= count - 1;
    const current = this.querySelector('[data-gallery-current]');
    if (current) current.textContent = String(this.#index + 1);
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      const active = Number(thumb.dataset.galleryThumb) === this.#index;
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('s-media-gallery')) customElements.define('s-media-gallery', SMediaGallery);
